// ---------------------------------------------------------------------------
// OrganizationStep — Shared for dso_org + retailer_org
// ---------------------------------------------------------------------------
// For retailer_org: when the backend includes a "not_listed" sentinel option
// (metadata.is_not_listed === true), a CTA is rendered below the org list.
// Selecting it expands an inline form to capture the retailer name, then
// submits { organization_id: "not_listed", requested_org_name: "<name>" }.
// ---------------------------------------------------------------------------

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OptionCard } from '@/components/shared/OptionCard';
import { SearchInput } from '@/components/shared/SearchInput';
import { StepHeading } from '@/components/shared/StepHeading';
import { CoverageNudge } from '@/components/shared/CoverageNudge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { t } from '@/i18n';
import { useCollectedData } from '@/hooks/useCollectedData';
import type { StepProps } from '@/types/steps';
import { getStepIcon } from '@/utils/stepIcons';
import { isSafeImageUrl } from '@/utils/url';
import styles from './OrganizationStep.module.css';

export const OrganizationStep = memo(function OrganizationStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const collectedData = useCollectedData();
  const isRetailer = stepId === 'retailer_org';

  const [query, setQuery] = useState('');

  // Pre-expand and pre-fill when returning to this step via back navigation
  // from the not-listed path (collected_data.retailer.requested_org_name set).
  const [showNotListedForm, setShowNotListedForm] = useState(() => {
    if (!isRetailer) return false;
    const retailer = collectedData['retailer'];
    if (typeof retailer !== 'object' || retailer === null) return false;
    const name = (retailer as Record<string, unknown>)['requested_org_name'];
    return typeof name === 'string' && name !== '';
  });
  const [requestedOrgName, setRequestedOrgName] = useState(() => {
    if (!isRetailer) return '';
    const retailer = collectedData['retailer'];
    if (typeof retailer !== 'object' || retailer === null) return '';
    const name = (retailer as Record<string, unknown>)['requested_org_name'];
    return typeof name === 'string' ? name : '';
  });
  const [nameError, setNameError] = useState('');

  // Fallback for when the session loads asynchronously after mount (e.g. tests).
  // The lazy initializers above handle the normal production case where the
  // session is already loaded before the step component mounts.
  const prefillAppliedRef = useRef(showNotListedForm);
  useEffect(() => {
    if (!isRetailer || prefillAppliedRef.current) return;
    const retailer = collectedData['retailer'];
    if (typeof retailer !== 'object' || retailer === null) return;
    const name = (retailer as Record<string, unknown>)['requested_org_name'];
    if (typeof name === 'string' && name !== '') {
      prefillAppliedRef.current = true;
      setShowNotListedForm(true);
      setRequestedOrgName(name);
    }
  }, [isRetailer, collectedData]);

  // Separate the sentinel from real orgs — same pattern as TariffStep / customOption.
  const { regularOptions, notListedOption } = useMemo(() => {
    const regular = options.options.filter((o) => o.metadata['is_not_listed'] !== true);
    const notListed = options.options.find((o) => o.metadata['is_not_listed'] === true) ?? null;
    return { regularOptions: regular, notListedOption: notListed };
  }, [options.options]);

  const previousOrgId = useMemo(() => {
    const group = stepId === 'dso_org' ? 'dso' : 'retailer';
    const nested = collectedData[group];
    if (typeof nested === 'object' && nested !== null) {
      const orgId = (nested as Record<string, unknown>)['organization_id'];
      return typeof orgId === 'string' || typeof orgId === 'number' ? String(orgId) : '';
    }
    return '';
  }, [collectedData, stepId]);

  const filtered = useMemo(() => {
    if (!query) return regularOptions;
    const lower = query.toLowerCase();
    return regularOptions.filter((o) => o.name.toLowerCase().includes(lower));
  }, [regularOptions, query]);

  const handleSelect = useCallback(
    (id: string) => {
      if (isSubmitting) return;
      setShowNotListedForm(false);
      setNameError('');
      onSubmit({ organization_id: id });
    },
    [onSubmit, isSubmitting],
  );

  const [focusInputOnOpen, setFocusInputOnOpen] = useState(false);

  const handleNotListedToggle = useCallback(() => {
    setShowNotListedForm((prev) => !prev);
    setFocusInputOnOpen(true);
    setNameError('');
  }, []);

  const handleNotListedSubmit = useCallback(() => {
    if (isSubmitting) return;
    const name = requestedOrgName.trim();
    if (!name) {
      setNameError(t('steps.retailer_org.notListedNameRequired'));
      return;
    }
    onSubmit({ organization_id: 'not_listed', requested_org_name: name });
  }, [requestedOrgName, onSubmit, isSubmitting]);

  const titleKey = `steps.${stepId}.title`;
  const subtitleKey = `steps.${stepId}.subtitle`;
  const logoWidth = isRetailer ? 120 : 48;
  const logoHeight = 48;
  const logoClass = isRetailer ? styles['retailerLogo'] : styles['logo'];

  return (
    <>
      <StepHeading title={t(titleKey)} subtitle={t(subtitleKey)} icon={getStepIcon(stepId)} />
      {regularOptions.length > 6 && (
        <div className={styles['search']}>
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder={t('common.search')}
            autoFocus
          />
        </div>
      )}
      <div className={styles['list']} role="listbox" aria-label={t(titleKey)}>
        {filtered.map((o) => (
          <OptionCard
            key={o.id}
            id={o.id}
            name={o.name}
            description={o.description || undefined}
            isSelected={o.id === previousOrgId}
            onClick={handleSelect}
            className={isRetailer ? styles['retailerCard'] : undefined}
            icon={
              isSafeImageUrl(o.metadata['logo_url']) ? (
                <img
                  src={o.metadata['logo_url']}
                  alt=""
                  width={logoWidth}
                  height={logoHeight}
                  className={logoClass}
                />
              ) : undefined
            }
          />
        ))}
      </div>
      <CoverageNudge coverage={options._coverage} />
      {notListedOption && (
        <>
          <button
            type="button"
            className={styles['notListedCta']}
            onClick={handleNotListedToggle}
            disabled={isSubmitting}
            aria-expanded={showNotListedForm}
          >
            {t('steps.common.retailerNotListed')} <span aria-hidden="true">→</span>
          </button>
          {showNotListedForm && (
            <form
              className={styles['notListedForm']}
              onSubmit={(e) => {
                e.preventDefault();
                handleNotListedSubmit();
              }}
              noValidate
            >
              <Input
                label={t('steps.retailer_org.notListedNameLabel')}
                name="requested_org_name"
                value={requestedOrgName}
                onChange={(e) => {
                  setRequestedOrgName(e.target.value);
                  if (nameError) setNameError('');
                }}
                placeholder={t('steps.retailer_org.notListedNamePlaceholder')}
                error={nameError}
                required
                disabled={isSubmitting}
                autoFocus={focusInputOnOpen}
              />
              <Button
                label={t('common.confirm')}
                type="submit"
                fullWidth
                isLoading={isSubmitting}
                disabled={isSubmitting}
              />
            </form>
          )}
        </>
      )}
    </>
  );
});
